// The signed-in Sentinel's Online/Offline switch for auto-routing, in the selected brand.
//
// One switch per brand, independent of the others: Online here gives them this brand's tickets
// while Kim is open (in any tab), whatever brand that tab shows. The state lives on the server,
// so it is the same in every tab and survives a reload. It is re-read whenever the brand changes
// and every time the socket (re)authenticates: a server or Redis restart can drop the switch, and
// an open tab must then show Offline rather than keep showing a stale Online.
//
// It also follows the live presence event the server sends to every open tab in the account
// whenever a switch changes, so the same Sentinel signed in on another device or tab sees their
// switch flip at once instead of after a reload.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { toast } from '@/components/ui/sonner';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { currentUserAtom } from '@/hooks/use-auth';
import { sentinelPresenceChangesAtom, websocketStatusAtom } from '@/stores/websocket.store';

export interface SentinelAvailability {
  /** Null until the server answers. */
  online: boolean | null;
  isSaving: boolean;
  setOnline: (online: boolean) => Promise<void>;
}

export function useSentinelAvailability(buId: string | null): SentinelAvailability {
  const [online, setOnlineState] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const socketLive = useAtomValue(websocketStatusAtom) === 'authenticated';
  const me = useAtomValue(currentUserAtom);
  const presenceChanges = useAtomValue(sentinelPresenceChangesAtom);
  const livePresence = buId && me?.id != null ? presenceChanges[`${buId}:${me.id}`] : undefined;
  // The last live value already applied: one recorded before this brand was opened is old news,
  // the server read below is fresher.
  const appliedPresence = useRef(livePresence);

  useEffect(() => {
    setOnlineState(null);
    if (!buId || !socketLive) return;
    let cancelled = false;
    void sentinelWorkspaceService
      .getAvailability({ bu_id: buId })
      .then((value) => {
        if (!cancelled) setOnlineState(value);
      })
      .catch(() => {
        if (!cancelled) setOnlineState(false);
      });
    return () => {
      cancelled = true;
    };
  }, [buId, socketLive]);

  useEffect(() => {
    appliedPresence.current = livePresence;
    // Only a change after this brand was opened; the brand's own read covers everything before.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buId]);

  useEffect(() => {
    if (livePresence === undefined || livePresence === appliedPresence.current) return;
    appliedPresence.current = livePresence;
    setOnlineState(livePresence);
  }, [livePresence]);

  const setOnline = useCallback(
    async (next: boolean) => {
      if (!buId) return;
      setIsSaving(true);
      try {
        setOnlineState(await sentinelWorkspaceService.setAvailability({ bu_id: buId }, next));
        toast.success(
          next
            ? 'You are online for this brand and will receive its tickets'
            : 'You are offline for this brand. Your tickets stay with you',
        );
      } catch {
        toast.error(next ? 'Could not go online. Please try again.' : 'Could not go offline. Please try again.');
      } finally {
        setIsSaving(false);
      }
    },
    [buId],
  );

  return { online, isSaving, setOnline };
}
